import { ContextPlugin } from '@ckeditor/ckeditor5-core/dist/index.js';
import { ObservableMixin, CKEditorError, EmitterMixin } from '@ckeditor/ckeditor5-utils/dist/index.js';

const DEFAULT_OPTIONS = {
    autoRefresh: true
};
const DEFAULT_TOKEN_REFRESH_TIMEOUT_TIME = 3600000;
class Token extends ObservableMixin() {
    /**
     * Initializes the token.
     */ init() {
        return new Promise((resolve, reject)=>{
            if (!this.value) {
                this.refreshToken().then(resolve).catch(reject);
                return;
            }
            if (this._options.autoRefresh) {
                this._registerRefreshTokenTimeout();
            }
            resolve(this);
        });
    }
    /**
     * Refresh token method. Useful in a method form as it can be override in tests.
     */ refreshToken() {
        return this._refresh().then((value)=>{
            this._validateTokenValue(value);
            this.set('value', value);
            if (this._options.autoRefresh) {
                this._registerRefreshTokenTimeout();
            }
            return this;
        });
    }
    /**
     * Destroys token instance. Stops refreshing.
     */ destroy() {
        clearTimeout(this._tokenRefreshTimeout);
    }
    /**
     * Checks whether the provided token follows the JSON Web Tokens (JWT) format.
     *
     * @param tokenValue The token to validate.
     */ _validateTokenValue(tokenValue) {
        // The token must be a string.
        const isString = typeof tokenValue === 'string';
        // The token must be a plain string without quotes ("").
        const isPlainString = !/^".*"$/.test(tokenValue);
        // JWT token contains 3 parts: header, payload, and signature.
        // Each part is separated by a dot.
        const isJWTFormat = isString && tokenValue.split('.').length === 3;
        if (!(isPlainString && isJWTFormat)) {
            /**
             * The provided token must follow the [JSON Web Tokens](https://jwt.io/introduction/) format.
             *
             * @error token-not-in-jwt-format
             */ throw new CKEditorError('token-not-in-jwt-format', this);
        }
    }
    /**
     * Registers a refresh token timeout for the time taken from token.
     */ _registerRefreshTokenTimeout() {
        const tokenRefreshTimeoutTime = this._getTokenRefreshTimeoutTime();
        clearTimeout(this._tokenRefreshTimeout);
        this._tokenRefreshTimeout = setTimeout(()=>{
            this.refreshToken();
        }, tokenRefreshTimeoutTime);
    }
    /**
     * Returns token refresh timeout time calculated from expire time in the token payload.
     *
     * If the token parse fails or the token payload doesn't contain, the default DEFAULT_TOKEN_REFRESH_TIMEOUT_TIME is returned.
     */ _getTokenRefreshTimeoutTime() {
        try {
            const [, binaryTokenPayload] = this.value.split('.');
            const { exp: tokenExpireTime } = JSON.parse(atob(binaryTokenPayload));
            if (!tokenExpireTime) {
                return DEFAULT_TOKEN_REFRESH_TIMEOUT_TIME;
            }
            const tokenRefreshTimeoutTime = Math.floor((tokenExpireTime * 1000 - Date.now()) / 2);
            return tokenRefreshTimeoutTime;
        } catch (err) {
            return DEFAULT_TOKEN_REFRESH_TIMEOUT_TIME;
        }
    }
    /**
     * Creates a initialized {@link module:cloud-services/token/token~Token} instance.
     *
     * @param tokenUrlOrRefreshToken Endpoint address to download the token or a callback that provides the token. If the
     * value is a function it has to match the {@link module:cloud-services/token/token~Token#refreshToken} interface.
     */ static create(tokenUrlOrRefreshToken, options = {}) {
        const token = new Token(tokenUrlOrRefreshToken, options);
        return token.init();
    }
    /**
     * Creates `Token` instance.
     * Method `init` should be called after using the constructor or use `create` method instead.
     *
     * @param tokenUrlOrRefreshToken Endpoint address to download the token or a callback that provides the token. If the
     * value is a function it has to match the {@link module:cloud-services/token/token~Token#refreshToken} interface.
     */ constructor(tokenUrlOrRefreshToken, options = {}){
        super();
        if (!tokenUrlOrRefreshToken) {
            /**
             * A `tokenUrl` must be provided as the first constructor argument.
             *
             * @error token-missing-token-url
             */ throw new CKEditorError('token-missing-token-url', this);
        }
        if (options.initValue) {
            this._validateTokenValue(options.initValue);
        }
        this.set('value', options.initValue);
        if (typeof tokenUrlOrRefreshToken === 'function') {
            this._refresh = tokenUrlOrRefreshToken;
        } else {
            this._refresh = ()=>defaultRefreshToken(tokenUrlOrRefreshToken);
        }
        this._options = {
            ...DEFAULT_OPTIONS,
            ...options
        };
    }
}
/**
 * This function is called in a defined interval by the {@link ~Token} class. It also can be invoked manually.
 * It should return a promise, which resolves with the new token value.
 * If any error occurs it should return a rejected promise with an error message.
 */ function defaultRefreshToken(tokenUrl) {
    return new Promise((resolve, reject)=>{
        const xhr = new XMLHttpRequest();
        xhr.open('GET', tokenUrl);
        xhr.addEventListener('load', ()=>{
            const statusCode = xhr.status;
            const xhrResponse = xhr.response;
            if (statusCode < 200 || statusCode > 299) {
                /**
                 * Cannot download new token from the provided url.
                 *
                 * @error token-cannot-download-new-token
                 */ return reject(new CKEditorError('token-cannot-download-new-token', null));
            }
            return resolve(xhrResponse);
        });
        xhr.addEventListener('error', ()=>reject(new Error('Network Error')));
        xhr.addEventListener('abort', ()=>reject(new Error('Abort')));
        xhr.send();
    });
}

const BASE64_HEADER_REG_EXP = /^data:(\S*?);base64,/;
class FileUploader extends EmitterMixin() {
    /**
     * Registers callback on `progress` event.
     */ onProgress(callback) {
        this.on('progress', (event, data)=>callback(data));
        return this;
    }
    /**
     * Registers callback on `error` event. Event is called once when error occurs.
     */ onError(callback) {
        this.once('error', (event, data)=>callback(data));
        return this;
    }
    /**
     * Aborts upload process.
     */ abort() {
        this.xhr.abort();
    }
    /**
     * Sends XHR request to API.
     */ send() {
        this._prepareRequest();
        this._attachXHRListeners();
        return this._sendRequest();
    }
    /**
     * Prepares XHR request.
     */ _prepareRequest() {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', this._apiAddress);
        xhr.setRequestHeader('Authorization', this._token.value);
        xhr.responseType = 'json';
        this.xhr = xhr;
    }
    /**
     * Attaches listeners to the XHR.
     */ _attachXHRListeners() {
        const xhr = this.xhr;
        const onError = (message)=>{
            return ()=>this.fire('error', message);
        };
        xhr.addEventListener('error', onError('Network Error'));
        xhr.addEventListener('abort', onError('Abort'));
        /* istanbul ignore else -- @preserve */ if (xhr.upload) {
            xhr.upload.addEventListener('progress', (event)=>{
                if (event.lengthComputable) {
                    this.fire('progress', {
                        total: event.total,
                        uploaded: event.loaded
                    });
                }
            });
        }
        xhr.addEventListener('load', ()=>{
            const statusCode = xhr.status;
            const xhrResponse = xhr.response;
            if (statusCode < 200 || statusCode > 299) {
                return this.fire('error', xhrResponse.message || xhrResponse.error);
            }
        });
    }
    /**
     * Sends XHR request.
     */ _sendRequest() {
        const formData = new FormData();
        const xhr = this.xhr;
        formData.append('file', this.file);
        return new Promise((resolve, reject)=>{
            xhr.addEventListener('load', ()=>{
                const statusCode = xhr.status;
                const xhrResponse = xhr.response;
                if (statusCode < 200 || statusCode > 299) {
                    if (xhrResponse.message) {
                        /**
                         * Uploading file failed.
                         *
                         * @error fileuploader-uploading-data-failed
                         */ return reject(new CKEditorError('fileuploader-uploading-data-failed', this, {
                            message: xhrResponse.message
                        }));
                    }
                    return reject(xhrResponse.error);
                }
                return resolve(xhrResponse);
            });
            xhr.addEventListener('error', ()=>reject(new Error('Network Error')));
            xhr.addEventListener('abort', ()=>reject(new Error('Abort')));
            xhr.send(formData);
        });
    }
    /**
     * Creates `FileUploader` instance.
     *
     * @param fileOrData A blob object or a data string encoded with Base64.
     * @param token Token used for authentication.
     * @param apiAddress API address.
     */ constructor(fileOrData, token, apiAddress){
        super();
        if (!fileOrData) {
            /**
             * File must be provided as the first argument.
             *
             * @error fileuploader-missing-file
             */ throw new CKEditorError('fileuploader-missing-file', null);
        }
        if (!token) {
            /**
             * Token must be provided as the second argument.
             *
             * @error fileuploader-missing-token
             */ throw new CKEditorError('fileuploader-missing-token', null);
        }
        if (!apiAddress) {
            /**
             * Api address must be provided as the third argument.
             *
             * @error fileuploader-missing-api-address
             */ throw new CKEditorError('fileuploader-missing-api-address', null);
        }
        this.file = _isBase64(fileOrData) ? _base64ToBlob(fileOrData) : fileOrData;
        this._token = token;
        this._apiAddress = apiAddress;
    }
}
/**
 * Transforms Base64 string data into file.
 *
 * @param base64 String data.
 */ function _base64ToBlob(base64, sliceSize = 512) {
    try {
        const contentType = base64.match(BASE64_HEADER_REG_EXP)[1];
        const base64Data = atob(base64.replace(BASE64_HEADER_REG_EXP, ''));
        const byteArrays = [];
        for(let offset = 0; offset < base64Data.length; offset += sliceSize){
            const slice = base64Data.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for(let i = 0; i < slice.length; i++){
                byteNumbers[i] = slice.charCodeAt(i);
            }
            byteArrays.push(new Uint8Array(byteNumbers));
        }
        return new Blob(byteArrays, {
            type: contentType
        });
    } catch (error) {
        /**
         * Problem with decoding Base64 image data.
         *
         * @error fileuploader-decoding-image-data-error
         */ throw new CKEditorError('fileuploader-decoding-image-data-error', null);
    }
}
/**
 * Checks that string is Base64.
 */ function _isBase64(string) {
    if (typeof string !== 'string') {
        return false;
    }
    const match = string.match(BASE64_HEADER_REG_EXP);
    return !!(match && match.length);
}

class UploadGateway {
    /**
     * Creates a {@link module:cloud-services/uploadgateway/fileuploader~FileUploader} instance that wraps
     * file upload process. The file is being sent at a time when the
     * {@link module:cloud-services/uploadgateway/fileuploader~FileUploader#send} method is called.
     *
     * ```ts
     * const token = await Token.create( 'https://token-endpoint' );
     * new UploadGateway( token, 'https://example.org' )
     * 	.upload( 'FILE' )
     * 	.onProgress( ( data ) => console.log( data ) )
     * 	.send()
     * 	.then( ( response ) => console.log( response ) );
     * ```
     *
     * @param {Blob|String} fileOrData A blob object or a data string encoded with Base64.
     * @returns {module:cloud-services/uploadgateway/fileuploader~FileUploader} Returns `FileUploader` instance.
     */ upload(fileOrData) {
        return new FileUploader(fileOrData, this._token, this._apiAddress);
    }
    /**
     * Creates `UploadGateway` instance.
     *
     * @param token Token used for authentication.
     * @param apiAddress API address.
     */ constructor(token, apiAddress){
        if (!token) {
            /**
             * Token must be provided.
             *
             * @error uploadgateway-missing-token
             */ throw new CKEditorError('uploadgateway-missing-token', null);
        }
        if (!apiAddress) {
            /**
             * Api address must be provided.
             *
             * @error uploadgateway-missing-api-address
             */ throw new CKEditorError('uploadgateway-missing-api-address', null);
        }
        this._token = token;
        this._apiAddress = apiAddress;
    }
}

class CloudServicesCore extends ContextPlugin {
    /**
     * @inheritDoc
     */ static get pluginName() {
        return 'CloudServicesCore';
    }
    /**
     * Creates the {@link module:cloud-services/token/token~Token} instance.
     *
     * @param tokenUrlOrRefreshToken Endpoint address to download the token or a callback that provides the token. If the
     * value is a function it has to match the {@link module:cloud-services/token/token~Token#refreshToken} interface.
     * @param options.initValue Initial value of the token.
     * @param options.autoRefresh Specifies whether to start the refresh automatically.
     */ createToken(tokenUrlOrRefreshToken, options) {
        return new Token(tokenUrlOrRefreshToken, options);
    }
    /**
     * Creates the {@link module:cloud-services/uploadgateway/uploadgateway~UploadGateway} instance.
     *
     * @param token Token used for authentication.
     * @param apiAddress API address.
     */ createUploadGateway(token, apiAddress) {
        return new UploadGateway(token, apiAddress);
    }
}

class CloudServices extends ContextPlugin {
    /**
     * @inheritDoc
     */ static get pluginName() {
        return 'CloudServices';
    }
    /**
     * @inheritDoc
     */ static get requires() {
        return [
            CloudServicesCore
        ];
    }
    /**
     * @inheritDoc
     */ async init() {
        const config = this.context.config;
        const options = config.get('cloudServices') || {};
        for (const [key, value] of Object.entries(options)){
            this[key] = value;
        }
        if (!this.tokenUrl) {
            this.token = null;
            return;
        }
        const cloudServicesCore = this.context.plugins.get('CloudServicesCore');
        this.token = await cloudServicesCore.createToken(this.tokenUrl).init();
        this._tokens.set(this.tokenUrl, this.token);
    }
    /**
     * Registers an additional authentication token URL for CKEditor Cloud Services or a callback to the token value promise. See the
     * {@link module:cloud-services/cloudservicesconfig~CloudServicesConfig#tokenUrl} for more details.
     *
     * @param tokenUrl The authentication token URL for CKEditor Cloud Services or a callback to the token value promise.
     */ async registerTokenUrl(tokenUrl) {
        // Reuse the token instance in case of multiple features using the same token URL.
        if (this._tokens.has(tokenUrl)) {
            return this.getTokenFor(tokenUrl);
        }
        const cloudServicesCore = this.context.plugins.get('CloudServicesCore');
        const token = await cloudServicesCore.createToken(tokenUrl).init();
        this._tokens.set(tokenUrl, token);
        return token;
    }
    /**
     * Returns an authentication token provider previously registered by {@link #registerTokenUrl}.
     *
     * @param tokenUrl The authentication token URL for CKEditor Cloud Services or a callback to the token value promise.
     */ getTokenFor(tokenUrl) {
        const token = this._tokens.get(tokenUrl);
        if (!token) {
            /**
             * The provided `tokenUrl` was not registered by {@link module:cloud-services/cloudservices~CloudServices#registerTokenUrl}.
             *
             * @error cloudservices-token-not-registered
             */ throw new CKEditorError('cloudservices-token-not-registered', this);
        }
        return token;
    }
    /**
     * @inheritDoc
     */ destroy() {
        super.destroy();
        for (const token of this._tokens.values()){
            token.destroy();
        }
    }
    constructor(){
        super(...arguments);
        /**
         * Other plugins use this token for the authorization process. It handles token requesting and refreshing.
         * Its value is `null` when {@link module:cloud-services/cloudservicesconfig~CloudServicesConfig#tokenUrl} is not provided.
         *
         * @readonly
         */ this.token = null;
        /**
         * A map of token object instances keyed by the token URLs.
         */ this._tokens = new Map();
    }
}

export { CloudServices, CloudServicesCore };
//# sourceMappingURL=index.js.map
